import {
    computed,
    DestroyRef,
    Directive,
    ElementRef,
    inject,
    input,
    type OnInit,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
    resolveColorContext,
    FOREGROUND_STYLES,
    type ContextFamily,
    type ContextTone,
} from '@quipex/shared/utils';
import { distinctUntilChanged } from 'rxjs';

type ButtonType = 'link' | 'button' | 'unstyled' | 'table';

export type ButtonSize = 'md' | 'sm' | 'xs';

@Directive({
    selector: 'button[qpxButton], a[qpxButton]',
    host: {
        '[class]': 'className()',
        '[type]': 'isButton ? type() : null',
        '[class.contextual]': 'context() !== undefined',
        '[style.--qpx-button-context-color]': 'surfaceColor()',
        '[style.--qpx-button-on-context-color]': 'foregroundColor()',
        '[style.--qpx-button-context-text-color]': 'textColor()',
        '[style.--mat-icon-button-icon-color]': 'iconColor()',
    },
})
export class QpxButton implements OnInit {
    //#region Dependency injection
    private readonly elementRef = inject(ElementRef, { host: true });
    private readonly matButtonRef = inject(MatButton, {
        host: true,
        optional: true,
    });
    private readonly matIconButtonRef = inject(MatIconButton, {
        host: true,
        optional: true,
    });
    private readonly destroyRef = inject(DestroyRef);
    //#endregion

    //#region Inputs
    /**
     * A directive property that defines the variant of the button.
     *
     * @example
     * ```html
     * <button qpxButton>Button</button>
     * ```
     *
     * @example
     * ```html
     * <a qpxButton="link">Link Button</a>
     * ```
     */
    public readonly variant = input<ButtonType, ButtonType | ''>('button', {
        alias: 'qpxButton',
        transform: (value) => value || 'button',
    });

    public readonly type = input<string>('button');

    public readonly size = input<ButtonSize>('md');

    public readonly shadow = input<boolean>(false);

    public readonly context = input<
        ContextFamily | undefined,
        ContextFamily | '' | undefined
    >(undefined, {
        transform: (value) => value || undefined,
    });
    private readonly context$ = toObservable(this.context);
    public readonly tone = input<ContextTone>('default');

    public readonly ripple = input<boolean>(false);
    private readonly ripple$ = toObservable(this.ripple);
    //#endregion

    //#region State
    /** Determines if the host element is a button tag */
    protected readonly isButton =
        this.elementRef.nativeElement.tagName.toLowerCase() === 'button';

    private readonly colors = computed(() => {
        const context = this.context();
        return context ? resolveColorContext(context, this.tone()) : undefined;
    });
    protected readonly surfaceColor = computed(() => {
        const color = this.colors()?.surface.variable;
        return color ? `var(${color})` : null;
    });
    protected readonly foregroundColor = computed(() => {
        const color = this.colors()?.foreground.variable;
        return color ? `var(${color})` : null;
    });
    protected readonly textColor = computed(() => {
        const color =
            !this.matIconButtonRef &&
            this.context() === 'primary' &&
            this.tone() === 'default'
                ? FOREGROUND_STYLES.ordinary.variable
                : this.colors()?.decorative.variable;
        return color ? `var(${color})` : null;
    });
    protected readonly iconColor = computed(() => {
        const color = this.matIconButtonRef
            ? this.colors()?.decorative.variable
            : undefined;
        return color ? `var(${color})` : null;
    });

    protected readonly className = computed(() => {
        const _className = ['qpx-button', this.size()];
        const context = this.context();

        const usingMatButton = this.matButtonRef ?? this.matIconButtonRef;

        if (this.variant() === 'unstyled') {
            return _className.join(' ');
        }

        if (this.variant() !== 'table' && !usingMatButton) {
            _className.push(this.textClassName());
        }

        if (this.variant() === 'link' || this.variant() === 'table') {
            _className.push('link');
        }

        if (context && !usingMatButton) {
            const colors = resolveColorContext(context, this.tone());
            _className.push(
                context === 'primary' && this.tone() === 'default'
                    ? FOREGROUND_STYLES.ordinary.className
                    : colors.decorative.className
            );
        }

        if (this.shadow()) {
            _className.push('shadow-xs');
        }

        return _className.join(' ');
    });
    //#endregion

    //#region Lifecycle hooks
    ngOnInit(): void {
        this.ripple$
            .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                if (this.matButtonRef) {
                    this.matButtonRef.disableRipple = !value;
                } else if (this.matIconButtonRef) {
                    this.matIconButtonRef.disableRipple = !value;
                }
            });

        this.context$
            .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                if (this.matButtonRef) {
                    this.matButtonRef.color = value;
                } else if (this.matIconButtonRef) {
                    this.matIconButtonRef.color = value;
                }
            });
    }
    //#endregion

    //#region Private methods
    private textClassName(): string {
        switch (this.size()) {
            case 'xs':
                return 'text-button-xs';
            case 'sm':
                return 'text-button-sm';
            case 'md':
                return 'text-button';
        }
    }
    //#endregion
}
